import { isWeb } from '~/utils/helper/device';
import { invokeChannel, onChannel, sendChannel } from '~/utils/constants';
import { deleteCookie, getCookie, setCookie } from '~/utils/cookie';
import { EDomain, keyCookie } from '~/types/enum';
import { parseJwt } from '~/utils/helper/cookie';
import {
  TOneClubGetUserTokenCode,
  TOneClubGetUserTokenResponse,
} from '~/api/oneClub/oneclub.type';
import {
  DOWNLOAD_PATH,
  TBaseDownloadPathSetting,
} from '~/components/Global/Dialog/BaseDownloadPathSettingDialog';

const getCurrentDomain = (): string => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return ''; // localhost 不需要 domain
  }

  if (hostname.includes('nani.com.tw')) {
    return EDomain.BOX3;
  }

  if (hostname.includes('oneclass.com.tw')) {
    return EDomain.ONECLASS;
  }
  // 預設使用 oneclass.com.tw
  return EDomain.ONECLASS;
};

const safeParseToken = (value: string): TOneClubGetUserTokenResponse | null => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (parsed && typeof parsed === 'object') {
      return parsed as TOneClubGetUserTokenResponse;
    }
    return null;
  } catch {
    return null;
  }
};

const deleteCookieFromAllDomains = (cookieName: string) => {
  const domains = [
    '',
    getCurrentDomain(), // 當前 domain
    EDomain.ONECLASS, // oneclass.com.tw
    EDomain.BOX3, // box3.nani.com.tw
  ];

  domains.forEach(domain => {
    if (domain) {
      deleteCookie(cookieName, domain);
    } else {
      deleteCookie(cookieName);
    }
  });
};

type TSetBaseDownloadPathSetting = { type: DOWNLOAD_PATH };

const updateTokenJwtCookie = (newJwt: string) => {
  const value = getCookie(keyCookie.ONECLASS_CLIENT);
  const parsedToken = value ? safeParseToken(value) : null;

  if (!parsedToken || !newJwt) return;

  setCookie(
    keyCookie.ONECLASS_CLIENT,
    JSON.stringify({ ...parsedToken, jwt: newJwt }),
    999,
    getCurrentDomain(),
  );
};

const webHandlers = {
  openExternal: (url: string) => {
    window.open(url);
  },
  getToken: () => {
    const value = getCookie(keyCookie.ONECLASS_CLIENT);
    const parsedToken = value ? safeParseToken(value) : null;

    if (!parsedToken) {
      if (value) {
        deleteCookieFromAllDomains(keyCookie.ONECLASS_CLIENT);
      }
      return '';
    }

    if (
      parsedToken.code === TOneClubGetUserTokenCode.success &&
      parsedToken.jwt
    ) {
      setCookie(
        keyCookie.ONECLASS_CLIENT,
        JSON.stringify(parsedToken),
        999,
        getCurrentDomain(),
      );
    }

    return parsedToken.jwt ?? '';
  },

  //已廢除
  setToken: (token: TOneClubGetUserTokenResponse) => {
    if (token.code === TOneClubGetUserTokenCode.success && token.jwt) {
      // const { exp } = parseJwt(token.jwt);
      // const expDays =
      //   Math.trunc(
      //     ((exp as number) - Math.trunc(new Date().valueOf() / 1000)) /
      //       60 /
      //       60 /
      //       24,
      //   ) ;
      setCookie(
        keyCookie.ONECLASS_CLIENT,
        JSON.stringify(token),
        undefined,
        getCurrentDomain(),
      );
    }
  },
  deleteToken: () => {
    deleteCookieFromAllDomains(keyCookie.ONECLASS_CLIENT);
  },
  clearOfflineDisplayName: () => {
    // 網頁版沒有本機離線 Header 狀態。
  },
  updateTokenJwt: updateTokenJwtCookie,
  getRememberedUsername: () => {
    return getCookie(keyCookie.REMEMBERED_USERNAME);
  },
  setRememberedUsername: (username: string) => {
    setCookie(keyCookie.REMEMBERED_USERNAME, username);
  },
  deleteRememberedUsername: () => {
    deleteCookie(keyCookie.REMEMBERED_USERNAME);
  },
  getBaseDownloadPathSetting: () => {},
  setBaseDownloadPathSetting: () => {},
  copyRightAlert: () => {
    alert(
      '版權聲明\n茲就南一書局企業股份有限公司（以下簡稱本公司）所提供之著作（包括但不限於文字、攝影、圖形、錄音、視聽暨其以Word或PDF等數位形式出版產品，及未來以各種形式發表之產品或內容），其著作財產權均歸屬於本公司所有或經具有合法權源之人授權使用，並受著作權法保護。各級學校及其擔任教學之人，基於為學校授課目的之必要範圍內，得在不逾越著作權法第46條、第46之1條及第65條規定下，合理使用本公司已公開發表之著作，但於利用時亦應以合理方式明示其出處。',
    );
  },
};

const desktopHandler = {
  openExternal: (url: string) => {
    window.api.send(sendChannel.openExternal, url);
  },
  getToken: () => {
    const value = getCookie(keyCookie.ONECLASS_CLIENT);
    const parsedToken = value ? safeParseToken(value) : null;

    if (!parsedToken) {
      if (value) {
        deleteCookieFromAllDomains(keyCookie.ONECLASS_CLIENT);
      }
      return '';
    }

    if (
      parsedToken.code === TOneClubGetUserTokenCode.success &&
      parsedToken.jwt
    ) {
      setCookie(
        keyCookie.ONECLASS_CLIENT,
        JSON.stringify(parsedToken),
        999,
        getCurrentDomain(),
      );
    }

    return parsedToken.jwt ?? '';
  },
  //已廢除
  setToken: (token: TOneClubGetUserTokenResponse) => {
    // window.api.invoke(invokeChannel.setToken, JSON.stringify(token.jwt));
    if (token.code === TOneClubGetUserTokenCode.success && token.jwt) {
      const { exp } = parseJwt(token.jwt);
      const remainingDays = (Number(exp) - Date.now() / 1000) / 86400;
      setCookie(
        keyCookie.ONECLASS_CLIENT,
        JSON.stringify(token),
        remainingDays,
        getCurrentDomain(),
      );
    }
  },
  deleteToken: () => {
    // window.api.invoke(invokeChannel.deleteToken);
    deleteCookieFromAllDomains(keyCookie.ONECLASS_CLIENT);
  },
  // 主動登出只清除離線 Header 的身分顯示；「這台電腦曾登入過」仍永久保留，
  // 讓使用者登出後斷網依然能開啟已下載資源。
  clearOfflineDisplayName: () => {
    void window.api.invoke(invokeChannel.clearOfflineDisplayName);
  },
  updateTokenJwt: updateTokenJwtCookie,
  getBaseDownloadPathSetting: (
    callback: (event: TBaseDownloadPathSetting) => void,
  ) => {
    window.api.on(onChannel.getBaseDownloadPathSetting, ([event]) => {
      callback(event as TBaseDownloadPathSetting);
    });
  },
  setBaseDownloadPathSetting: ({ type }: TSetBaseDownloadPathSetting) => {
    window.api.invoke(invokeChannel.setBaseDownloadPath, type);
  },
  getRememberedUsername: async () => {
    const username = (await window.api.invoke(
      invokeChannel.getRememberedUsername,
    )) as string;
    return username;
  },
  setRememberedUsername: (username: string) => {
    window.api.invoke(invokeChannel.setRememberedUsername, username);
  },
  deleteRememberedUsername: () => {
    window.api.invoke(invokeChannel.deleteRememberedUsername);
  },
  copyRightAlert: () => {
    window.api.send(sendChannel.dialogMessage, {
      title: '',
      message:
        '版權聲明\n茲就南一書局企業股份有限公司（以下簡稱本公司）所提供之著作（包括但不限於文字、攝影、圖形、錄音、視聽暨其以Word或PDF等數位形式出版產品，及未來以各種形式發表之產品或內容），其著作財產權均歸屬於本公司所有或經具有合法權源之人授權使用，並受著作權法保護。各級學校及其擔任教學之人，基於為學校授課目的之必要範圍內，得在不逾越著作權法第46條、第46之1條及第65條規定下，合理使用本公司已公開發表之著作，但於利用時亦應以合理方式明示其出處。',
    });
  },
};

export const useCommon = () => {
  const {
    openExternal,
    getToken,
    setToken,
    deleteToken,
    clearOfflineDisplayName,
    updateTokenJwt,
    getBaseDownloadPathSetting,
    setBaseDownloadPathSetting,
    getRememberedUsername,
    setRememberedUsername,
    deleteRememberedUsername,
    copyRightAlert,
  } = isWeb ? webHandlers : desktopHandler;
  return {
    openExternal,
    getToken,
    setToken,
    deleteToken,
    clearOfflineDisplayName,
    updateTokenJwt,
    getBaseDownloadPathSetting,
    setBaseDownloadPathSetting,
    getRememberedUsername,
    setRememberedUsername,
    deleteRememberedUsername,
    copyRightAlert,
  };
};
